import React from 'react';
import { ArrowLeft, Shield, Database, Lock, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { legalBrand } from './legalBrand';

const DataProcessingAddendum = ({ brand }) => {
  const b = legalBrand(brand);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link 
            to={b.home} 
            className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            {b.homeLabel}
          </Link>
          <h1 className="text-4xl font-light text-gray-900 mb-2">Data Processing Addendum</h1>
          <p className="text-gray-600">Effective Date: January 1, 1988</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 space-y-8">
          
          {/* Notice Banner */}
          <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-r-lg">
            <div className="flex items-start">
              <Shield className="text-purple-600 mr-3 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-purple-900 mb-2">Your Data, Your Rights</h3>
                <p className="text-gray-800 leading-relaxed">
                  This Data Processing Addendum (DPA) explains how we collect, process, and protect your personal data 
                  in accordance with our commitment to transparency and your privacy rights.
                </p>
              </div>
            </div>
          </div>

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction & Scope</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Data Processing Addendum ("DPA") forms an integral part of our Privacy Policy and governs the processing 
              of personal data collected through keeperofthefiyah.com ("the Site"). This DPA applies to all data subjects 
              whose personal data we process, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Newsletter subscribers (INSPIYAH MAIL)</li>
              <li>Website visitors</li>
              <li>Contact form submitters</li>
              <li>Any individual whose data we process in connection with our services</li>
            </ul>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Definitions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">2.1 Key Terms</h3>
                <ul className="space-y-3 ml-4">
                  <li className="text-gray-700">
                    <strong>Data Controller:</strong> CT / KEEPER OF THE FIYAH, who determines the purposes and means of processing personal data
                  </li>
                  <li className="text-gray-700">
                    <strong>Data Subject:</strong> Any identified or identifiable natural person whose personal data we process
                  </li>
                  <li className="text-gray-700">
                    <strong>Personal Data:</strong> Any information relating to an identified or identifiable individual, including but not limited to: name, email address, IP address, usage data
                  </li>
                  <li className="text-gray-700">
                    <strong>Processing:</strong> Any operation performed on personal data, including collection, storage, use, disclosure, or deletion
                  </li>
                  <li className="text-gray-700">
                    <strong>Data Processor:</strong> Any third party that processes personal data on our behalf (currently: MongoDB for database services)
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Data We Collect & Process</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">3.1 Data You Provide</h3>
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="flex items-start mb-4">
                <Database className="text-purple-600 mr-3 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Email Subscription (INSPIYAH MAIL)</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>Email address</li>
                    <li>Subscription timestamp</li>
                    <li>Subscription source (portfolio_website)</li>
                    <li>Subscription status (active/inactive)</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start">
                <Database className="text-purple-600 mr-3 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Contact Forms (Future)</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Message content</li>
                    <li>Submission timestamp</li>
                  </ul>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">3.2 Data We Collect Automatically</h3>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start">
                <Database className="text-purple-600 mr-3 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Analytics Data</h4>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>Welcome Gate view count (anonymous - no personal identifiers)</li>
                    <li>Page views (aggregate counts only)</li>
                    <li>Browser session data (temporary, not stored)</li>
                  </ul>
                  <p className="text-gray-600 text-sm mt-3 italic">
                    Note: We do NOT collect IP addresses, device IDs, or tracking cookies beyond functional session management.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Purpose of Processing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Purpose of Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We process personal data solely for the following legitimate purposes:
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="font-medium text-gray-800 mb-2">4.1 Email Newsletter Service (INSPIYAH MAIL)</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Sending spiritual insights, YAHFFIRMATIONS, and content updates</li>
                  <li>Managing subscription status and preferences</li>
                  <li>Providing unsubscribe functionality</li>
                </ul>
                <p className="text-gray-600 text-sm mt-2 italic">Legal Basis: Consent (explicit opt-in)</p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="font-medium text-gray-800 mb-2">4.2 Website Analytics</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Understanding site engagement and visitor counts</li>
                  <li>Improving user experience and content</li>
                  <li>Measuring conversion rates (visitors to subscribers)</li>
                </ul>
                <p className="text-gray-600 text-sm mt-2 italic">Legal Basis: Legitimate interest (website improvement)</p>
              </div>

              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="font-medium text-gray-800 mb-2">4.3 Communication & Support (Future)</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Responding to inquiries and requests</li>
                  <li>Providing customer support</li>
                  <li>Processing feedback and suggestions</li>
                </ul>
                <p className="text-gray-600 text-sm mt-2 italic">Legal Basis: Consent & legitimate interest</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-gray-800 font-medium mb-2">🚫 What We Will NEVER Do:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Sell, rent, or trade your personal data to third parties</li>
                <li>Use your data for AI training or machine learning</li>
                <li>Share your data for marketing purposes without explicit consent</li>
                <li>Process your data for purposes other than stated above</li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security Measures</h2>
            
            <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <Lock className="text-purple-600 mr-3 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-3">Our Security Commitment</h3>
                  <p className="text-gray-800 leading-relaxed">
                    We implement industry-standard technical and organizational measures to protect your personal data 
                    against unauthorized access, loss, destruction, or alteration.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-medium text-gray-800 mb-3">5.1 Technical Measures</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-6">
              <li><strong>Encryption:</strong> All data transmitted via HTTPS/TLS encryption</li>
              <li><strong>Database Security:</strong> MongoDB hosted with secure authentication and access controls</li>
              <li><strong>Access Controls:</strong> Password-protected analytics dashboard with token-based authentication</li>
              <li><strong>Session Management:</strong> Secure token generation with 24-hour expiration</li>
              <li><strong>Regular Updates:</strong> Software and security patches applied promptly</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">5.2 Organizational Measures</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access Limitation:</strong> Only authorized personnel have access to personal data</li>
              <li><strong>Data Minimization:</strong> We collect only data necessary for stated purposes</li>
              <li><strong>Regular Reviews:</strong> Quarterly security and privacy assessments</li>
              <li><strong>Incident Response:</strong> Documented procedures for data breach response</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-800 mb-2">Email Subscribers</h3>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Retention Period:</strong> Until you unsubscribe or request deletion<br />
                  <strong>Deletion:</strong> Within 30 days of unsubscribe request<br />
                  <strong>Inactive Accounts:</strong> Accounts inactive for 2+ years will be reviewed for deletion
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-800 mb-2">Analytics Data</h3>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Retention Period:</strong> Indefinitely (aggregate counts only, no personal data)<br />
                  <strong>Note:</strong> This data is anonymous and cannot be tied to individuals
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-800 mb-2">Authentication Tokens (Analytics Login)</h3>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Retention Period:</strong> 24 hours<br />
                  <strong>Automatic Deletion:</strong> Expired tokens are automatically removed from database
                </p>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Data Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              As a data subject, you have the following rights regarding your personal data:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                <h3 className="font-medium text-purple-900 mb-2">✓ Right to Access</h3>
                <p className="text-gray-700 text-sm">
                  Request a copy of all personal data we hold about you
                </p>
              </div>

              <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                <h3 className="font-medium text-orange-900 mb-2">✓ Right to Rectification</h3>
                <p className="text-gray-700 text-sm">
                  Correct any inaccurate or incomplete personal data
                </p>
              </div>

              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <h3 className="font-medium text-red-900 mb-2">✓ Right to Erasure</h3>
                <p className="text-gray-700 text-sm">
                  Request deletion of your personal data ("right to be forgotten")
                </p>
              </div>

              <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                <h3 className="font-medium text-yellow-900 mb-2">✓ Right to Restrict Processing</h3>
                <p className="text-gray-700 text-sm">
                  Limit how we process your personal data
                </p>
              </div>

              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <h3 className="font-medium text-blue-900 mb-2">✓ Right to Data Portability</h3>
                <p className="text-gray-700 text-sm">
                  Receive your data in a structured, machine-readable format
                </p>
              </div>

              <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="font-medium text-green-900 mb-2">✓ Right to Withdraw Consent</h3>
                <p className="text-gray-700 text-sm">
                  Unsubscribe or withdraw consent at any time
                </p>
              </div>
            </div>

            <div className="mt-6 p-6 bg-purple-50 border-2 border-purple-200 rounded-lg">
              <h3 className="font-medium text-purple-900 mb-3">How to Exercise Your Rights</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                To exercise any of these rights, please contact us at:
              </p>
              <p className="text-gray-800 font-medium">Email: {b.privacy}</p>
              <p className="text-gray-700 mt-2 text-sm">
                Subject line: "Data Rights Request - World of INSPIYAH"
              </p>
              <p className="text-gray-600 text-sm mt-3 italic">
                We will respond to all valid requests within 30 days.
              </p>
            </div>
          </section>

          {/* Data Processors */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Third-Party Data Processors</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We engage the following third-party processors to help us provide our services:
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="font-medium text-gray-800 mb-3">Current Data Processors</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-medium text-gray-800">MongoDB Atlas</h4>
                  <p className="text-gray-700 text-sm mt-1"><strong>Purpose:</strong> Database hosting and management</p>
                  <p className="text-gray-700 text-sm"><strong>Data Processed:</strong> Email addresses, subscription data, analytics counts</p>
                  <p className="text-gray-700 text-sm"><strong>Location:</strong> United States</p>
                  <p className="text-gray-700 text-sm"><strong>Security:</strong> SOC 2 Type II certified, encryption at rest and in transit</p>
                </div>

                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-medium text-gray-800">Emergent Cloud Services</h4>
                  <p className="text-gray-700 text-sm mt-1"><strong>Purpose:</strong> Application hosting and infrastructure</p>
                  <p className="text-gray-700 text-sm"><strong>Data Processed:</strong> Website content, session data (temporary)</p>
                  <p className="text-gray-700 text-sm"><strong>Location:</strong> United States</p>
                  <p className="text-gray-700 text-sm"><strong>Security:</strong> HTTPS/TLS encryption, secure container infrastructure</p>
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed">
              All processors are bound by data processing agreements that ensure they process personal data only in accordance 
              with our instructions and implement appropriate security measures.
            </p>
          </section>

          {/* Data Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your personal data may be transferred to and processed in the United States, where our servers and service providers 
              are located. We ensure that any international transfers comply with applicable data protection laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>For EU/UK Data Subjects:</strong> Data transfers are based on Standard Contractual Clauses (SCCs) approved 
              by the European Commission, or other appropriate safeguards as required by GDPR.
            </p>
          </section>

          {/* Breach Notification */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Data Breach Notification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In the unlikely event of a data breach that affects your personal data, we will:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Assess the breach:</strong> Determine the scope, nature, and risk level within 24 hours</li>
              <li><strong>Notify authorities:</strong> Report to relevant data protection authorities within 72 hours (as required)</li>
              <li><strong>Notify affected individuals:</strong> Inform you directly via email within 72 hours if high risk</li>
              <li><strong>Provide guidance:</strong> Offer clear instructions on protective measures you can take</li>
              <li><strong>Remediate:</strong> Implement measures to prevent future breaches</li>
            </ul>
          </section>

          {/* Changes to DPA */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This DPA</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Data Processing Addendum periodically to reflect changes in our data processing practices 
              or legal requirements. Material changes will be communicated via:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Email notification to subscribers</li>
              <li>Prominent notice on the website</li>
              <li>Updated "Effective Date" at the top of this document</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Continued use of our services after changes constitutes acceptance of the updated DPA.
            </p>
          </section>

          {/* Contact & Questions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Questions & Contact</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you have any questions about this Data Processing Addendum, our data practices, or wish to exercise 
              your data rights, please contact us:
            </p>
            
            <div className="p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-lg border-2 border-purple-200">
              <div className="flex items-start">
                <FileText className="text-purple-600 mr-3 flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="text-gray-800 font-semibold mb-1">Data Controller</p>
                  <p className="text-gray-800 font-medium">CT / KEEPER OF THE FIYAH</p>
                  <p className="text-gray-700 mt-3"><strong>Email:</strong> {b.privacy}</p>
                  <p className="text-gray-700"><strong>Website:</strong> keeperofthefiyah.com</p>
                  <p className="text-gray-600 text-sm mt-4 italic">
                    All inquiries will be responded to within 5-7 business days.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Related Policies */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Related Policies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This DPA should be read in conjunction with our other governance documents:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/privacy-policy" className="p-4 bg-purple-50 rounded-lg border border-purple-200 hover:border-purple-400 transition-colors">
                <p className="font-medium text-purple-900">Privacy Policy</p>
                <p className="text-sm text-gray-600 mt-1">How we collect and use data</p>
              </Link>
              <Link to="/ai-usage-policy" className="p-4 bg-orange-50 rounded-lg border border-orange-200 hover:border-orange-400 transition-colors">
                <p className="font-medium text-orange-900">AI Usage Policy</p>
                <p className="text-sm text-gray-600 mt-1">Content protection measures</p>
              </Link>
              <Link to="/terms-of-service" className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 hover:border-yellow-400 transition-colors">
                <p className="font-medium text-yellow-900">Terms of Service</p>
                <p className="text-sm text-gray-600 mt-1">Website usage terms</p>
              </Link>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default DataProcessingAddendum;
